// src/components/WinLoseOverlay.tsx

import React, {
  useEffect,
  useState,
} from "react";

import Confetti from "react-confetti";

import "./WinLoseOverlay.css";


// =====================================================
// TYPES
// =====================================================

interface Props {
  isWinner: boolean;

  onRematch: () => void;
  onBackToGameHouse: () => void;

  rematchWaiting?: boolean;
}


// =====================================================
// WIN / LOSE OVERLAY
// =====================================================

const WinLoseOverlay: React.FC<Props> = ({
  isWinner,
  onRematch,
  onBackToGameHouse,
  rematchWaiting = false,
}) => {

  const [
    showConfetti,
    setShowConfetti,
  ] = useState(
    isWinner
  );


  // ===================================================
  // CONFETTI TIMER
  // ===================================================

  useEffect(() => {

    if (!isWinner) {

      setShowConfetti(
        false
      );

      return;
    }


    setShowConfetti(
      true
    );


    const timer =
      window.setTimeout(
        () => {

          setShowConfetti(
            false
          );

        },
        5000
      );


    return () => {

      window.clearTimeout(
        timer
      );
    };

  }, [
    isWinner,
  ]);


  // ===================================================
  // UI
  // ===================================================

  return (

    <div
      className={`
        win-lose-overlay
        ${
          isWinner
            ? "victory"
            : "defeat"
        }
      `}
      role="dialog"
      aria-modal="true"
      aria-labelledby="game-result-title"
    >

      {/* ============================================= */}
      {/* CONFETTI                                      */}
      {/* ============================================= */}

      {showConfetti && (

        <div className="result-confetti">

          <Confetti
            recycle={false}
            numberOfPieces={320}
            gravity={0.12}
          />

        </div>

      )}


      {/* ============================================= */}
      {/* BACKGROUND GLOW                               */}
      {/* ============================================= */}

      <div
        className="result-ambient-glow"
        aria-hidden="true"
      />


      {/* ============================================= */}
      {/* CINEMATIC RINGS                               */}
      {/* ============================================= */}

      <div
        className="
          result-ring
          result-ring-outer
        "
        aria-hidden="true"
      />

      <div
        className="
          result-ring
          result-ring-middle
        "
        aria-hidden="true"
      />

      <div
        className="
          result-ring
          result-ring-inner
        "
        aria-hidden="true"
      />


      {/* ============================================= */}
      {/* SIDE DECORATIONS                              */}
      {/* ============================================= */}

      <div
        className="
          result-ornament
          result-ornament-left
        "
        aria-hidden="true"
      />

      <div
        className="
          result-ornament
          result-ornament-right
        "
        aria-hidden="true"
      />


      {/* ============================================= */}
      {/* RESULT CONTENT                                */}
      {/* ============================================= */}

      <div className="result-cinematic-content">

        <div className="result-eyebrow">

          {isWinner
            ? "MATCH COMPLETE"
            : "MATCH ENDED"}

        </div>


        <div
          className="result-emblem"
          aria-hidden="true"
        >

          {isWinner
            ? "♛"
            : "♠"}

        </div>


        <h1
          id="game-result-title"
          className="result-title"
        >

          {isWinner
            ? "VICTORY"
            : "DEFEAT"}

        </h1>


        <div className="result-divider">

          <span />

          <strong>
            ◆
          </strong>

          <span />

        </div>


        <p className="result-message">

          {rematchWaiting
            ? "Waiting for your opponent to accept the rematch..."
            : isWinner
              ? "The table is yours."
              : "A worthy game. The table awaits your return."}

        </p>


        <div className="result-brand">
          FASOOR
        </div>


        {/* =========================================== */}
        {/* ACTIONS                                     */}
        {/* =========================================== */}

        <div className="result-actions">

          <button
            type="button"
            className="result-primary-button"
            onClick={onRematch}
            disabled={rematchWaiting}
          >

            <span>
              {rematchWaiting
                ? "Waiting..."
                : "🔁 Rematch"}
            </span>

          </button>


          <button
            type="button"
            className="result-secondary-button"
            onClick={onBackToGameHouse}
          >

            <span>
              Back to Game House
            </span>

          </button>

        </div>

      </div>


      <div
        className="result-floor-glow"
        aria-hidden="true"
      />

    </div>

  );
};


export default WinLoseOverlay;
